import { useEffect, useRef, useState } from "react";
import { X, Sparkles, RotateCcw, Check, Image as ImageIcon, Lock } from "lucide-react";
import { usePro } from "@/context/ProContext";
import UpgradeModal from "@/components/pro/UpgradeModal";

const FILTERS = [
  { id: "original", name: "Original",  css: "" },
  { id: "warm",     name: "Cálido",    css: "saturate(1.15) sepia(0.18) contrast(1.05)" },
  { id: "cool",     name: "Frío",      css: "saturate(0.9) hue-rotate(-15deg) brightness(1.04)" },
  { id: "drama",    name: "Drama",     css: "contrast(1.25) saturate(1.1) brightness(0.95)" },
  { id: "soft",     name: "Suave",     css: "contrast(0.92) brightness(1.06) saturate(0.95)" },
  { id: "mono",     name: "Mono",      css: "grayscale(1) contrast(1.08)" },
  { id: "sepia",    name: "Sepia",     css: "sepia(0.85) contrast(1.05) saturate(0.85)" },
  { id: "vintage",  name: "Vintage",   css: "sepia(0.35) saturate(1.2) hue-rotate(-8deg) contrast(1.05)" },
  { id: "pop",      name: "Pop",       css: "saturate(1.5) contrast(1.1) brightness(1.02)" },
];

// Signature filter — only PRO. Recognizable cinematic look impossible to replicate by accident.
const OVAMX_ORIGINAL = {
  id: "ovamx_original",
  name: "Ovamx Original",
  css: "contrast(1.18) saturate(1.18) brightness(1.04) hue-rotate(-4deg) sepia(0.06)",
  pro: true,
};

export default function PhotoEditor({ open, initialSrc = null, onClose, onApply }) {
  const pro = usePro();
  const [src, setSrc] = useState(initialSrc);
  const [filter, setFilter] = useState("original");
  const [brightness, setBrightness] = useState(100);
  const [contrast, setContrast] = useState(100);
  const [saturate, setSaturate] = useState(100);
  const [sharp, setSharp] = useState(0); // 0..100
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const fileRef = useRef(null);
  const canvasRef = useRef(null);
  const imgRef = useRef(null);

  useEffect(() => { if (open) setSrc(initialSrc); }, [open, initialSrc]);

  if (!open) return null;

  const isOriginal = filter === OVAMX_ORIGINAL.id;
  const presetCss = isOriginal
    ? OVAMX_ORIGINAL.css
    : (FILTERS.find((f) => f.id === filter)?.css || "");
  const adjustCss = `brightness(${brightness}%) contrast(${contrast + sharp * 0.4}%) saturate(${saturate}%)`;
  const fullCss = `${presetCss} ${adjustCss}`.trim();

  const onPick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setSrc(ev.target.result);
    reader.readAsDataURL(file);
  };

  const reset = () => {
    setFilter("original");
    setBrightness(100);
    setContrast(100);
    setSaturate(100);
    setSharp(0);
  };

  const aiAuto = () => {
    setBrightness(106);
    setContrast(110);
    setSaturate(112);
    setSharp(28);
  };

  const apply = () => {
    if (!src || !imgRef.current) {
      onApply?.(null);
      return;
    }
    const img = imgRef.current;
    const canvas = canvasRef.current || document.createElement("canvas");
    const ratio = img.naturalWidth && img.naturalHeight ? img.naturalWidth / img.naturalHeight : 1;
    const w = Math.min(1600, img.naturalWidth || 1200);
    const h = Math.round(w / (ratio || 1));
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.filter = fullCss;
    try {
      ctx.drawImage(img, 0, 0, w, h);
      const dataUrl = canvas.toDataURL("image/jpeg", 0.88);
      onApply?.({ src: dataUrl, signature: isOriginal });
    } catch {
      onApply?.({ src, filter: fullCss, tainted: true, signature: isOriginal });
    }
    onClose?.();
  };

  const tryFilter = (id) => {
    if (id === OVAMX_ORIGINAL.id && !pro?.is_pro) {
      setUpgradeOpen(true);
      return;
    }
    setFilter(id);
  };

  return (
    <div data-testid="photo-editor" className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-ink/55 backdrop-blur-sm" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-5xl bg-cream-card border hairline rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[92vh]">
        <header className="flex items-center justify-between px-6 py-4 border-b hairline">
          <div className="flex items-center gap-2">
            <ImageIcon className="w-4 h-4 text-coral" />
            <h3 className="font-serif text-2xl">Editor de foto</h3>
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-mid ml-2">nativo</span>
          </div>
          <button onClick={onClose} data-testid="photo-editor-close" className="w-8 h-8 rounded-full hover:bg-cream-deep flex items-center justify-center" aria-label="Cerrar">
            <X className="w-4 h-4" />
          </button>
        </header>

        <div className="grid grid-cols-12 gap-0 flex-1 min-h-0">
          {/* Preview */}
          <div className="col-span-12 lg:col-span-8 bg-cream-deep flex items-center justify-center p-6 min-h-[320px]">
            {src ? (
              <img
                ref={imgRef}
                src={src}
                alt="preview"
                crossOrigin="anonymous"
                style={{ filter: fullCss }}
                className="max-h-[60vh] max-w-full object-contain rounded-md shadow-lg"
                data-testid="photo-editor-preview"
              />
            ) : (
              <div className="text-center">
                <button
                  onClick={() => fileRef.current?.click()}
                  data-testid="photo-editor-pick"
                  className="inline-flex items-center gap-2 bg-ink text-cream rounded-full px-6 py-3 text-sm hover:bg-coral transition"
                >
                  <ImageIcon className="w-4 h-4" />
                  Elegir foto
                </button>
                <p className="mt-3 text-xs text-ink-mid">JPG, PNG · hasta 10 MB</p>
              </div>
            )}
            <input ref={fileRef} type="file" accept="image/*" onChange={onPick} className="hidden" />
          </div>

          {/* Controls */}
          <aside className="col-span-12 lg:col-span-4 border-l hairline overflow-y-auto p-5 space-y-5 bg-cream">
            {/* AI auto */}
            <button
              onClick={aiAuto}
              data-testid="photo-editor-ai"
              className="w-full inline-flex items-center justify-center gap-2 bg-coral text-cream rounded-full py-3 text-sm hover:bg-coral-hover transition"
            >
              <Sparkles className="w-4 h-4" />
              IA: ajuste automático
            </button>

            {/* Filters */}
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-mid mb-2">Filtros</p>
              <div className="grid grid-cols-3 gap-2">
                {/* Signature filter — first slot, distinguished */}
                <button
                  data-testid="filter-ovamx_original"
                  onClick={() => tryFilter(OVAMX_ORIGINAL.id)}
                  className={`relative rounded-lg overflow-hidden border-2 transition ${
                    filter === OVAMX_ORIGINAL.id ? "border-coral ring-2 ring-coral/25" : "border-transparent hover:border-coral/60"
                  }`}
                  title="Ovamx Original — solo PRO"
                >
                  <div className="aspect-square w-full overflow-hidden bg-gradient-to-br from-coral via-coral-hover to-ink relative">
                    {src ? (
                      <img src={src} alt="Ovamx Original" crossOrigin="anonymous" style={{ filter: OVAMX_ORIGINAL.css }} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full" />
                    )}
                    {!pro?.is_pro && (
                      <span className="absolute top-1 right-1 inline-flex items-center gap-0.5 bg-ink/80 text-cream text-[8px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-full">
                        <Lock className="w-2.5 h-2.5" /> PRO
                      </span>
                    )}
                  </div>
                  <span className="absolute inset-x-0 bottom-0 bg-gradient-to-r from-coral to-ink text-cream text-[10px] py-1 text-center font-medium">
                    ✦ Original
                  </span>
                </button>
                {FILTERS.map((f) => (
                  <button
                    key={f.id}
                    data-testid={`filter-${f.id}`}
                    onClick={() => setFilter(f.id)}
                    className={`relative rounded-lg overflow-hidden border-2 transition ${
                      filter === f.id ? "border-coral ring-2 ring-coral/25" : "border-transparent hover:border-ink/15"
                    }`}
                    title={f.name}
                  >
                    <div className="aspect-square w-full overflow-hidden bg-cream-deep">
                      {src ? (
                        <img src={src} alt={f.name} crossOrigin="anonymous" style={{ filter: f.css }} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-cream-deep to-ink/20" />
                      )}
                    </div>
                    <span className="absolute inset-x-0 bottom-0 bg-ink/70 text-cream text-[10px] py-1 text-center font-medium">
                      {f.name}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Sliders */}
            <div className="space-y-3">
              <Slider testid="slider-brightness" label="Brillo"     value={brightness} setValue={setBrightness} min={50}  max={150} />
              <Slider testid="slider-contrast"   label="Contraste"  value={contrast}   setValue={setContrast}   min={50}  max={150} />
              <Slider testid="slider-saturate"   label="Saturación" value={saturate}   setValue={setSaturate}   min={0}   max={200} />
              <Slider testid="slider-sharp"      label="Nitidez"    value={sharp}      setValue={setSharp}      min={0}   max={100} suffix="" />
            </div>

            <div className="flex items-center justify-between pt-3 border-t hairline">
              <button onClick={reset} data-testid="photo-editor-reset" className="inline-flex items-center gap-2 text-xs text-ink-mid hover:text-ink transition">
                <RotateCcw className="w-3.5 h-3.5" />
                Restablecer
              </button>
              <button onClick={apply} data-testid="photo-editor-apply" className="inline-flex items-center gap-2 bg-ink text-cream rounded-full px-5 py-2.5 text-sm hover:bg-coral transition">
                <Check className="w-4 h-4" />
                Aplicar
              </button>
            </div>
          </aside>
        </div>
        <canvas ref={canvasRef} className="hidden" />
      </div>

      <UpgradeModal
        open={upgradeOpen}
        onClose={() => setUpgradeOpen(false)}
        reason="El Filtro «Ovamx Original» es exclusivo de Ovamx PRO — el sello que la audiencia reconoce."
      />
    </div>
  );
}

function Slider({ label, value, setValue, min, max, suffix = "%", testid }) {
  return (
    <label className="block">
      <div className="flex items-center justify-between mb-1.5">
        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-mid">{label}</span>
        <span className="font-mono text-[11px] text-ink">{value}{suffix}</span>
      </div>
      <input
        data-testid={testid}
        type="range" min={min} max={max} value={value}
        onChange={(e) => setValue(parseInt(e.target.value, 10))}
        className="w-full accent-coral"
      />
    </label>
  );
}
